import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Clock, MapPin, Smartphone, Mail, User, 
  CheckCircle2, AlertCircle, ShieldCheck, Lock, 
  Camera, Upload, Info, FileWarning, Loader2
} from "lucide-react";

export default function AttendanceForm() {
  const [now, setNow] = useState(new Date());
  
  // Initialize status based on current time immediately to avoid flash of wrong state
  const [status, setStatus] = useState(() => {
    const current = new Date();
    const minutes = current.getHours() * 60 + current.getMinutes();
    if (minutes < 480) return "BEFORE";
    if (minutes >= 480 && minutes < 600) return "OPEN";
    return "CLOSED";
  }); 
  
  const [step, setStep] = useState("FORM"); 
  const [error, setError] = useState("");
  const [generatedOtp, setGeneratedOtp] = useState("");
  const [otp, setOtp] = useState("");
  const [photo, setPhoto] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);

  // Location state
  const [isGettingLocation, setIsGettingLocation] = useState(false);

  const [form, setForm] = useState({
    name: "",
    email: "",
    mobile: "",
    location: "",
  });

  /* ================= TIME LOGIC (RESTORED) ================= */
  useEffect(() => {
    const timer = setInterval(() => {
      const current = new Date();
      setNow(current);
      
      const minutesOfDay = current.getHours() * 60 + current.getMinutes();
      const startMinutes = 8 * 60; // 8:00 AM
      const endMinutes = 10 * 60;  // 10:00 AM

      if (minutesOfDay < startMinutes) {
          setStatus("BEFORE");
      } else if (minutesOfDay >= startMinutes && minutesOfDay < endMinutes) {
          setStatus("OPEN");
      } else {
          setStatus("CLOSED");
      }

    }, 1000);
    return () => clearInterval(timer);
  }, []);

  /* ================= HELPERS ================= */
  const getCountdown = () => {
    if (status === "CLOSED") return "CLOSED";

    // Target time depends on status
    const targetHours = status === "BEFORE" ? 8 : 10;
    
    const target = new Date(now);
    target.setHours(targetHours, 0, 0, 0);
    
    const diff = target - now;
    if (diff <= 0) return "00:00:00";

    const h = Math.floor((diff / (1000 * 60 * 60)) % 24);
    const m = Math.floor((diff / (1000 * 60)) % 60);
    const s = Math.floor((diff / 1000) % 60);

    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  const getStatusConfig = () => {
    switch (status) {
      case "OPEN": 
        return { color: "text-emerald-600", bg: "bg-emerald-50", border: "border-emerald-200", badge: "bg-emerald-100 text-emerald-700", label: "Accepting Responses" };
      case "BEFORE": 
        return { color: "text-blue-600", bg: "bg-blue-50", border: "border-blue-200", badge: "bg-blue-100 text-blue-700", label: "Starts Soon" };
      default: 
        return { color: "text-rose-600", bg: "bg-rose-50", border: "border-rose-200", badge: "bg-rose-100 text-rose-700", label: "Form Closed" };
    }
  };

  /* ================= HANDLERS ================= */
  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  // Auto Capture Location Logic
  const handleGetLocation = () => {
    setIsGettingLocation(true);
    setError("");

    if (!navigator.geolocation) {
      setError("Geolocation is not supported by your browser");
      setIsGettingLocation(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        const locString = `Lat: ${latitude.toFixed(6)}, Long: ${longitude.toFixed(6)}`;
        setForm(prev => ({ ...prev, location: locString }));
        setIsGettingLocation(false);
      },
      (err) => {
        console.error(err);
        setError("Unable to retrieve location. Please allow location access.");
        setIsGettingLocation(false);
      },
      { enableHighAccuracy: true }
    );
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhoto(URL.createObjectURL(file));
      setPhotoFile(file);
    }
  };

  /* ================= REQUEST OTP ================= */
  const handleSubmitAttendance = async (e) => {
    e.preventDefault();

    if (!photoFile) {
      setError("Selfie upload is mandatory.");
      return;
    }
    
    if (!form.location) {
        setError("Please capture your location before submitting.");
        return;
    }

    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("email", form.email);
      fd.append("mobile", form.mobile);
      fd.append("location", form.location);
      fd.append("photo", photoFile);

      const res = await fetch("https://api-dg3-core-inv5.adminpaymentdailygo.co.in/api/attendance/request-otp", {
        method: "POST",
        body: fd,
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message);

      setGeneratedOtp(""); 
      setError("");
      setStep("OTP");
    } catch (err) {
      setError(err.message || "Failed to request OTP");
    }
  };

  const handleVerifyOtp = async () => {
    try {
      const res = await fetch("https://api-dg3-core-inv5.adminpaymentdailygo.co.in/api/attendance/verify-otp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: form.email,
          otp,
        }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message);

      setError("");
      setStep("SUCCESS");
    } catch (err) {
      setError(err.message || "Invalid Code");
    }
  };

  const config = getStatusConfig();

  return (
   <div className="min-h-screen relative flex items-center justify-center bg-slate-50 text-slate-800 font-sans p-4">
      
      {/* Background Decor */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-0 w-[800px] h-[800px] bg-blue-100/50 rounded-full blur-[100px] -translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-purple-100/50 rounded-full blur-[100px] translate-x-1/3 translate-y-1/3" />
      </div>

      <motion.div 
        layout
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative w-full max-w-6xl z-10"
      >
        <div className="bg-white rounded-[2rem] shadow-2xl shadow-slate-200/50 overflow-hidden border border-slate-100 flex flex-col lg:flex-row min-h-[700px]">
          
          {/* ================= LEFT PANEL: FORM ================= */}
          <div className="w-full lg:w-7/12 p-8 md:p-12 flex flex-col relative">
            
            {/* Header */}
            <div className="flex justify-between items-center mb-8">
              <div>
                <h1 className="text-3xl font-bold tracking-tight text-slate-900">Daily Attendance</h1>
                <p className="text-slate-500 mt-1 flex items-center gap-2">
                  <Clock className="w-4 h-4" /> {now.toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                </p>
              </div>
              <span className={`px-4 py-1.5 rounded-full text-xs font-bold tracking-wider uppercase border ${config.badge} ${config.border}`}>
                {config.label}
              </span>
            </div>

            <AnimatePresence mode="wait">
              {step === "FORM" && (
                <motion.form
                  key="form"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onSubmit={handleSubmitAttendance}
                  className="space-y-6 flex-1 flex flex-col"
                >
                  {/* Selfie Upload Area (CAMERA ONLY) */}
                  <div className="relative group">
                    <input 
                        type="file" 
                        accept="image/*"
                        capture="user" // Forces selfie camera
                        disabled={status !== "OPEN"}
                        onChange={handlePhotoUpload}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10 disabled:cursor-not-allowed"
                    />
                    <div className={`w-full h-40 rounded-2xl border-2 border-dashed transition-all duration-300 flex flex-col items-center justify-center gap-3
                        ${photo 
                            ? "border-emerald-400 bg-emerald-50" 
                            : "border-slate-300 bg-slate-50 group-hover:border-blue-400 group-hover:bg-blue-50"
                        }
                    `}>
                        {photo ? (
                            <div className="relative w-full h-full p-2">
                                <img src={photo} alt="Selfie" className="w-full h-full object-cover rounded-xl shadow-sm" />
                                <div className="absolute top-4 right-4 bg-white p-1.5 rounded-full shadow-md text-emerald-600">
                                    <CheckCircle2 className="w-5 h-5" />
                                </div>
                            </div>
                        ) : (
                            <>
                                <div className="p-3 bg-white rounded-full shadow-sm text-slate-400 group-hover:text-blue-500 transition-colors">
                                    <Camera className="w-6 h-6" />
                                </div>
                                <div className="text-center">
                                    <p className="text-sm font-semibold text-slate-700">
                                      {status === "OPEN" ? "Take Selfie" : "Form Locked"}
                                    </p>
                                    <p className="text-xs text-slate-400">
                                      {status === "OPEN" ? "Camera access required" : "Wait for 8:00 AM"}
                                    </p>
                                </div>
                            </>
                        )}
                    </div>
                  </div>

                  {/* Inputs */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div className="space-y-1">
                        <label className="text-xs font-semibold text-slate-500 uppercase ml-1">Full Name</label>
                        <input name="name" required disabled={status !== "OPEN"} onChange={handleChange} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all disabled:opacity-60 disabled:cursor-not-allowed" placeholder="John Doe" />
                    </div>
                    <div className="space-y-1">
                        <label className="text-xs font-semibold text-slate-500 uppercase ml-1">Mobile</label>
                        <input name="mobile" required disabled={status !== "OPEN"} onChange={handleChange} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all disabled:opacity-60 disabled:cursor-not-allowed" placeholder="+91 98765 43210" />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div className="space-y-1">
                        <label className="text-xs font-semibold text-slate-500 uppercase ml-1">Email</label>
                        <input name="email" type="email" required disabled={status !== "OPEN"} onChange={handleChange} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all disabled:opacity-60 disabled:cursor-not-allowed" placeholder="employee@company.com" />
                    </div>

                    {/* Auto Location Field */}
                    <div className="space-y-1">
                        <label className="text-xs font-semibold text-slate-500 uppercase ml-1 flex justify-between">
                            <span>Current Location</span>
                            {status === "OPEN" && <span className="text-blue-500 text-[10px] cursor-pointer" onClick={handleGetLocation}>Refresh</span>}
                        </label>
                        <div className="relative">
                            <input 
                                name="location" 
                                readOnly 
                                value={form.location}
                                className="w-full pl-10 pr-4 py-3 bg-slate-100 border border-slate-200 rounded-xl text-slate-600 focus:outline-none cursor-not-allowed" 
                                placeholder="Click button to detect" 
                            />
                            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                                <MapPin className="w-4 h-4" />
                            </div>
                            <button
                                type="button"
                                onClick={handleGetLocation}
                                disabled={isGettingLocation || status !== "OPEN"}
                                className="absolute right-2 top-1/2 -translate-y-1/2 bg-white hover:bg-slate-50 text-blue-600 text-xs px-3 py-1.5 rounded-lg border border-slate-200 shadow-sm transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isGettingLocation ? (
                                    <>
                                        <Loader2 className="w-3 h-3 animate-spin" /> Detecting...
                                    </>
                                ) : (
                                    "Detect"
                                )}
                            </button>
                        </div>
                    </div>
                  </div>

                  {error && (
                    <div className="p-3 rounded-lg bg-rose-50 border border-rose-200 text-rose-600 text-sm flex items-center gap-2">
                        <AlertCircle className="w-4 h-4" /> {error}
                    </div>
                  )}

                  <div className="pt-2 mt-auto">
                    <button
                      type="submit"
                      disabled={status !== "OPEN" || isGettingLocation}
                      className={`w-full py-4 rounded-xl font-bold tracking-wide transition-all shadow-lg ${
                        status === "OPEN" 
                          ? "bg-slate-900 text-white hover:bg-slate-800 hover:shadow-xl hover:-translate-y-0.5" 
                          : "bg-slate-200 text-slate-400 cursor-not-allowed shadow-none"
                      }`}
                    >
                      {status === "OPEN" ? "Verify & Submit" : "Attendance Locked"}
                    </button>
                  </div>
                </motion.form>
              )}

              {step === "OTP" && (
                <motion.div
                  key="otp"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="flex flex-col items-center justify-center h-full text-center space-y-6 flex-1"
                >
                  <div className="p-4 bg-blue-50 rounded-full text-blue-600 mb-2">
                    <ShieldCheck className="w-12 h-12" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-slate-900">Verification Required</h3>
                    <p className="text-slate-500">Enter the code sent to your mobile.</p>
                  </div>

                  <div className="py-2 px-4 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-700 text-sm font-mono">
                    DEMO OTP: <strong>{generatedOtp}</strong>
                  </div>

                  <input
                    type="text"
                    maxLength={6}
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    className="w-48 text-center text-3xl font-mono tracking-widest py-3 border-b-2 border-slate-200 focus:border-blue-500 outline-none bg-transparent transition-colors"
                    placeholder="------"
                  />

                  {error && <p className="text-rose-500 text-sm font-medium">{error}</p>}

                  <button
                    onClick={handleVerifyOtp}
                    className="w-full max-w-xs py-3 rounded-xl bg-blue-600 text-white font-bold hover:bg-blue-700 transition-all"
                  >
                    Confirm Attendance
                  </button>
                </motion.div>
              )}

              {step === "SUCCESS" && (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="flex flex-col items-center justify-center h-full text-center flex-1"
                >
                  <div className="w-24 h-24 bg-emerald-100 rounded-full flex items-center justify-center mb-6">
                    <CheckCircle2 className="w-12 h-12 text-emerald-600" />
                  </div>
                  <h2 className="text-3xl font-bold text-slate-900 mb-2">You are marked Present!</h2>
                  <p className="text-slate-500 mb-8 max-w-sm">
                    Attendance successfully recorded for {now.toLocaleDateString()}.
                  </p>
                  <div className="w-full bg-slate-50 p-6 rounded-2xl border border-slate-200">
                    <div className="flex justify-between items-center mb-2">
                        <span className="text-slate-500 text-sm">Entry Time</span>
                        <span className="font-mono font-semibold text-slate-800">{now.toLocaleTimeString()}</span>
                    </div>
                    <div className="flex justify-between items-center">
                        <span className="text-slate-500 text-sm">Status</span>
                        <span className="font-bold text-emerald-600 bg-emerald-100 px-2 py-0.5 rounded text-xs">PRESENT</span>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* ================= RIGHT PANEL: NOTES & INFO ================= */}
          <div className="w-full lg:w-5/12 bg-slate-50 border-t lg:border-t-0 lg:border-l border-slate-200 p-8 md:p-12 flex flex-col">
            
            {/* Timer Card */}
            <div className={`p-6 rounded-2xl border mb-8 text-center ${config.bg} ${config.border}`}>
                <p className={`text-xs font-bold uppercase tracking-widest mb-2 ${config.color}`}>
                    {status === "OPEN" ? "Time Remaining" : "Status"}
                </p>
                <div className="text-5xl font-mono font-bold text-slate-800 tracking-tighter tabular-nums">
                    {getCountdown()}
                </div>
                <p className="text-slate-400 text-sm mt-2 font-medium">
                    {status === "OPEN" ? "Mark before 10:00 AM" : "Window: 8:00 AM - 10:00 AM"}
                </p>
            </div>

            {/* Policies */}
            <div className="flex-1">
                <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-4 flex items-center gap-2">
                    <Info className="w-4 h-4" /> Important Guidelines
                </h3>
                
                <ul className="space-y-4 text-sm text-slate-600 leading-relaxed">
                    <li className="flex gap-3">
                        <span className="mt-1 w-1.5 h-1.5 rounded-full bg-slate-400 shrink-0" />
                        <span>Employee must mark attendance with registered email, user name, and phone number.</span>
                    </li>
                    <li className="flex gap-3">
                        <span className="mt-1 w-1.5 h-1.5 rounded-full bg-slate-400 shrink-0" />
                        <span>If marked as leave, the day will be recorded as leave and a mail will be sent to your registered email.</span>
                    </li>
                    <li className="flex gap-3">
                        <span className="mt-1 w-1.5 h-1.5 rounded-full bg-slate-400 shrink-0" />
                        <span>Attendance window: <strong className="text-slate-800">8:00 AM to 10:00 AM</strong>.</span>
                    </li>
                    <li className="flex gap-3">
                        <span className="mt-1 w-1.5 h-1.5 rounded-full bg-slate-400 shrink-0" />
                        <span>Address link must be valid; it will be verified upon submission.</span>
                    </li>
                    <li className="flex gap-3">
                        <span className="mt-1 w-1.5 h-1.5 rounded-full bg-slate-400 shrink-0" />
                        <span><strong className="text-slate-800">Selfie Upload</strong> is mandatory for verification.</span>
                    </li>
                    <li className="flex gap-3">
                        <span className="mt-1 w-1.5 h-1.5 rounded-full bg-slate-400 shrink-0" />
                        <span>OTP verification is required to confirm attendance.</span>
                    </li>
                    <li className="flex gap-3">
                        <span className="mt-1 w-1.5 h-1.5 rounded-full bg-slate-400 shrink-0" />
                        <span>Please mark attendance 10 mins before the end time (10:00 AM) to avoid delays.</span>
                    </li>
                </ul>

                <div className="mt-8 p-4 bg-amber-50 border border-amber-200 rounded-xl flex gap-3">
                    <FileWarning className="w-5 h-5 text-amber-600 shrink-0" />
                    <p className="text-xs text-amber-800 font-medium leading-relaxed">
                        Once leave is marked, it is considered final. No modifications will be done for absence. The company is not responsible for missed markings.
                    </p>
                </div>
            </div>

            <div className="mt-8 text-center lg:text-left">
                <p className="text-xs text-slate-300 font-mono">HR-SYS-VER-4.2</p>
            </div>

          </div>
        </div>
      </motion.div>
    </div>
  );
}